import { useMemo } from 'react';
import { productionEnvironment, useBuddi } from '@bentley/pw-config';
import { useConfigContext } from '../../../context';
import { createWorkAreaEncodedSelectionStateFromUrlParams } from '../../../services/encode/encode';
import {
  pw365LinkBuilder,
  synchroLinkBuilder
} from '../../../services/support/connectedServiceLinks';
import { SolutionType } from '../data/useSolutionType';
import { readUniversalLinkParams } from '../searchParams/searchParams';

export function useWebLink(solutionType?: SolutionType): string {
  const { config } = useConfigContext();

  let pw365Url = useBuddi('ProjectWise365.URL', config.BUDDIRegionCode);
  let pwWebUrl = useBuddi('ProjectWiseWAC.URL', config.BUDDIRegionCode);
  const synchroUrl = useBuddi('OpenConstructionPortal', config.BUDDIRegionCode);

  // configure urls to work for release environments
  if (productionEnvironment() == 'release') {
    if (pwWebUrl) {
      const wacUrlParts = pwWebUrl.split('-');
      pwWebUrl =
        'https://qa-release-' +
        wacUrlParts.slice(1, wacUrlParts.length).join('-');
    }

    if (pw365Url) {
      const pw365UrlParts = pw365Url.split('-');
      pw365Url =
        'https://release-' +
        pw365UrlParts.slice(1, pw365UrlParts.length).join('-');
    }
  }

  const { projectId } = readUniversalLinkParams();
  const encodedSelectionState =
    createWorkAreaEncodedSelectionStateFromUrlParams();

  const synchroLink = useMemo((): string => {
    if (!synchroUrl || !projectId || !encodedSelectionState) {
      return '';
    }

    const synchroLink = synchroLinkBuilder(
      synchroUrl,
      projectId,
      encodedSelectionState
    );
    return synchroLink;
  }, [encodedSelectionState, projectId, synchroUrl]);

  const pw365Link = useMemo((): string => {
    if (!pw365Url || !projectId || !encodedSelectionState) {
      return '';
    }

    const pw365Link = pw365LinkBuilder(
      pw365Url,
      projectId,
      encodedSelectionState
    );
    return pw365Link;
  }, [encodedSelectionState, projectId, pw365Url]);

  const pwWebLink = useMemo((): string => {
    if (!pwWebUrl || !projectId || !encodedSelectionState) {
      return '';
    }

    const baseUrl =
      productionEnvironment() == 'localhost' &&
      config.DataAccessLevel == 'WorkArea'
        ? `${window.location.origin}/`
        : pwWebUrl;

    const pwWebLink = `${baseUrl}context/${projectId}?link=${encodedSelectionState}`;
    return pwWebLink;
  }, [config.DataAccessLevel, encodedSelectionState, projectId, pwWebUrl]);

  const webLink = useMemo((): string => {
    switch (solutionType) {
      case 'OpenConstruction': {
        return synchroLink;
      }
      case 'ProjectWise365': {
        return pw365Link;
      }
      case 'web':
      default: {
        return pwWebLink;
      }
    }
  }, [pw365Link, pwWebLink, solutionType, synchroLink]);

  return webLink;
}
